import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { Badge } from '../../ui/badge'
import { Plus, Trash2 } from 'lucide-react'
import { SimpleConfigurationItem } from '../../../model/Event'

export interface ConfigItemListProps {
  title: string
  description: string
  icon: React.ReactNode
  items: SimpleConfigurationItem[]
  onAdd: (name: string) => Promise<void>
  onEdit: (id: number, name: string) => Promise<void>
  onDelete: (item: SimpleConfigurationItem) => void
  saving: boolean
  itemType: string
  canEdit: boolean
}

/**
 * Inline add / rename / delete list for the simple name-only configuration
 * items on an event (locations, positions). Shared by the Schedule Config tab
 * and the setup wizard so both behave identically.
 */
export const ConfigItemList: React.FC<ConfigItemListProps> = ({
  title,
  description,
  icon,
  items,
  onAdd,
  onEdit,
  onDelete,
  saving,
  itemType,
  canEdit,
}) => {
  const [newItemName, setNewItemName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')

  const handleAdd = async () => {
    if (!newItemName.trim()) return
    await onAdd(newItemName.trim())
    setNewItemName('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAdd()
  }

  const startEditing = (item: SimpleConfigurationItem) => {
    setEditingId(item.id)
    setEditingName(item.name)
  }

  const handleEditBlur = async () => {
    if (editingId === null) return
    const trimmed = editingName.trim()
    const original = items.find((i) => i.id === editingId)
    if (trimmed && trimmed !== original?.name) {
      await onEdit(editingId, trimmed)
    }
    setEditingId(null)
    setEditingName('')
  }

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleEditBlur()
    if (e.key === 'Escape') {
      setEditingId(null)
      setEditingName('')
    }
  }

  const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {sortedItems.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {editingId === item.id ? (
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={handleEditBlur}
                        onKeyDown={handleEditKeyDown}
                        autoFocus
                        className="h-8"
                      />
                    ) : canEdit ? (
                      <span
                        className="cursor-pointer hover:underline"
                        onClick={() => startEditing(item)}
                        title="Click to edit"
                      >
                        {item.name}
                      </span>
                    ) : (
                      <span>{item.name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={item.state === 'REFERENCED' ? 'default' : 'secondary'}>
                      {item.state === 'REFERENCED' ? 'In Use' : 'Unused'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {canEdit && item.state === 'UNREFERENCED' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(item)}
                        disabled={saving}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {sortedItems.length === 0 && (
          <p className="text-center py-4 text-sm text-muted-foreground">
            No {itemType}s yet. Add one below.
          </p>
        )}

        {canEdit && (
          <div className="flex gap-2">
            <Input
              placeholder={`New ${itemType} name...`}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
            />
            <Button onClick={handleAdd} disabled={saving || !newItemName.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
